import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, HelpCircle, RotateCcw, Smile } from 'lucide-react';
import { env } from '../../config/env';
import { PaymentStatusPanel } from '../../payment-gateways/core/components/PaymentStatusPanel';
import { LoadingState } from '../../payment-gateways/core/components/LoadingState';
import { getGatewayById } from '../../payment-gateways/core/registry';
import { usePaymentStatusPolling } from '../../payment-gateways/core/hooks/usePaymentStatusPolling';
import {
  readPendingOrder,
  clearPendingOrder,
} from '../../payment-gateways/core/utils/pendingOrderStore';
import { redirectToBookingStatus } from '../../payment-gateways/core/utils/bookingStatusRedirect';
import {
  PaymentError,
  PaymentErrorCode,
} from '../../payment-gateways/core/utils/errors';
import { PaymentErrorScreen } from '../../payment-gateways/core/components/PaymentErrorScreen';
import type {
  GatewayReturnPayload,
  OrderContext,
} from '../../payment-gateways/core/types';

type CallbackState =
  | 'checking'
  | 'polling'
  | 'cancelled'
  | 'timeout'
  | 'unverifiable'
  | { error: PaymentError };

/**
 * The one route every gateway returns through (the plan's §2) —
 * Razorpay via a client-side navigation after its inline checkout settles,
 * 2C2P via a real cross-origin redirect. Either way, this page never
 * trusts what the return URL claims about itself: it always re-asks
 * `/bookings/payment-status`, which is what makes a stuck or cancelled
 * payment recoverable instead of the silent blank page the old
 * ThailandPayment.js had no equivalent of at all.
 */
export default function PaymentCallbackPage() {
  const [state, setState] = useState<CallbackState>('checking');
  const { start } = usePaymentStatusPolling();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const search = new URLSearchParams(window.location.search);
    const gatewayId = search.get('gateway');
    const orderId = search.get('order_id');

    if (!gatewayId || !orderId) {
      setState({
        error: new PaymentError(
          PaymentErrorCode.INVALID_ORDER,
          'This return link is missing required details.'
        ),
      });
      return;
    }

    const manifestEntry = getGatewayById(gatewayId);
    const order = readPendingOrder();

    if (!manifestEntry) {
      setState({
        error: new PaymentError(
          PaymentErrorCode.CONFIG_ERROR,
          `Unknown payment method "${gatewayId}".`
        ),
      });
      return;
    }

    manifestEntry
      .load()
      .then(async ({ default: adapter }) => {
        const payload: GatewayReturnPayload = adapter.parseReturnParams(search);
        await settle(order, orderId, payload);
      })
      .catch(() => {
        setState({
          error: new PaymentError(
            PaymentErrorCode.CONFIG_ERROR,
            `Could not load ${gatewayId}.`
          ),
        });
      });

    async function settle(
      order: OrderContext | null,
      orderId: string,
      payload: GatewayReturnPayload
    ) {
      if (payload.claimedStatus === 'cancelled') {
        setState('cancelled');
        return;
      }

      if (!order || order.orderId !== orderId) {
        setState('unverifiable');
        return;
      }

      setState('polling');

      const outcome = await start({
        // 2C2P's return doesn't reliably carry a gateway payment id in this
        // repo's current knowledge of its params — falling back to orderId
        // is a best-effort assumption pending backend confirmation of its
        // actual return contract; Razorpay always has one.
        paymentId: payload.gatewayPaymentId ?? orderId,
        dbPtr: order.dbPtr,
        authToken: order.authToken,
      });

      clearPendingOrder();

      if (outcome.success) {
        redirectToBookingStatus(order, {
          success: true,
          gatewayId: payload.gatewayId,
          gatewayPaymentId: payload.gatewayPaymentId,
          appointmentData: outcome.appointmentId,
        });
        return;
      }

      if (outcome.timedOut) {
        setState('timeout');
        redirectToBookingStatus(order, {
          success: false,
          gatewayId: payload.gatewayId,
          gatewayPaymentId: payload.gatewayPaymentId,
        });
        return;
      }

      setState('timeout');
    }
    // Runs once on mount — the params it reads are the ones this page was
    // navigated to with, not something that changes during its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-app-gradient relative min-h-screen w-full">
      <CallbackBody state={state} />
    </div>
  );
}

function CallbackBody({ state }: { state: CallbackState }) {
  const navigate = useNavigate();

  if (typeof state === 'object') {
    return (
      <PaymentErrorScreen error={state.error} onBack={() => navigate(-1)} />
    );
  }

  switch (state) {
    case 'checking':
    case 'polling':
      return (
        <LoadingState
          fullPage
          label="Confirming your payment…"
          sublabel="This may take a moment. Please don’t refresh or close the window."
        />
      );
    case 'cancelled':
      return (
        <PaymentStatusPanel
          icon={Smile}
          title="Payment cancelled"
          message="No worries — if anything was charged, it'll be refunded automatically within 5–7 days. You can pick up right where you left off."
          primaryAction={{
            label: 'Try again',
            icon: RotateCcw,
            onClick: () => navigate(-1),
          }}
          secondaryAction={{
            label: 'Go to back',
            icon: ArrowLeft,
            // Cross-origin (the booking app), so a real link, not navigate().
            href: `${env.VITE_PUBLIC_URL}/portal/appointment-details`,
          }}
        />
      );
    case 'timeout':
      return (
        <PaymentStatusPanel
          icon={Clock}
          title="Still confirming your payment"
          message="We haven't heard back yet. If you were charged, we'll confirm your booking as soon as it clears — no need to pay again."
          primaryAction={{
            label: 'Try again',
            icon: RotateCcw,
            onClick: () => navigate(-1),
          }}
        />
      );
    case 'unverifiable':
      return (
        <PaymentStatusPanel
          icon={HelpCircle}
          title="We couldn't confirm that automatically"
          message="Your session may have expired. Check your bookings, or get in touch with your payment reference and we'll sort it out."
          primaryAction={{
            label: 'Go back',
            onClick: () => navigate(-1),
          }}
        />
      );
    default:
      return null;
  }
}
